/**
 * Caption edit mode — determines whether an AI rewrite should be applied
 * instantly (draft / failed / etc.) or staged for explicit Keep / Revert
 * comparison (scheduled posts).
 *
 * Spec §5.3 / founder decision #3: scheduled posts are high-stakes so a
 * rewrite is staged in local state; the user sees Original ⇄ Revised and
 * picks Keep (commits) or Revert (discards). Every other editable status
 * keeps the fast path: apply immediately + Undo toast.
 *
 * Mirrors the `compareModeForStatus` policy from `./post/compareMode` but
 * expressed as `'stage' | 'apply'` to make the unit-test contract explicit
 * (the two maps are intentionally isomorphic: 'compare' → 'stage', 'instant'
 * → 'apply').
 */
export type CaptionEditMode = 'stage' | 'apply';

/** The only status that stages the rewrite for explicit Keep / Revert. */
export const STAGED_CAPTION_STATUSES = ['scheduled'] as const;

/**
 * Returns `'stage'` for scheduled posts (show inline compare), `'apply'`
 * for everything else (instant apply + Undo toast).
 */
export function captionEditModeForStatus(status: string | null | undefined): CaptionEditMode {
  return status != null &&
    STAGED_CAPTION_STATUSES.includes(status as (typeof STAGED_CAPTION_STATUSES)[number])
    ? 'stage'
    : 'apply';
}
